import { apiClient } from './client';
import type {
  Activity,
  ActivityWithAssociations,
  CreateActivityDto,
  UpdateActivityDto,
  KeyParticipantDto,
  TimelineDto,
  SortOption,
  ActivityItem,
  ReorderActivitiesRequest,
  AssignLeadsRequest,
  AssignKeyParticipantsRequest,
  TagVendorsRequest,
  UpdateStatusRequest,
  AddItemRequest,
  UpdateItemRequest
} from '@/types/activity.types';

export class ActivityService {
  // ============================================================================
  // CORE CRUD OPERATIONS
  // ============================================================================

  async getActivitiesByEvent(eventId: number, sort?: SortOption): Promise<Activity[]> {
    const params = sort ? { sort } : {};
    const response = await apiClient.get(`/events/${eventId}/activities`, { params });
    return response.data.data || [];
  }

  async getActivitiesByCelebration(celebrationId: number, sort?: SortOption): Promise<Activity[]> {
    const params = sort ? { sort } : {};
    const response = await apiClient.get(`/celebrations/${celebrationId}/scheduled-activities`, { params });
    return response.data.data || [];
  }

  async getActivity(id: number): Promise<ActivityWithAssociations> {
    const response = await apiClient.get(`/activities/${id}`);
    return response.data.data;
  }

  async createActivity(eventId: number, activityData: Omit<CreateActivityDto, 'event_id'>): Promise<Activity> {
    const response = await apiClient.post(`/events/${eventId}/activities`, activityData);
    return response.data.data;
  }

  async updateActivity(id: number, activityData: UpdateActivityDto): Promise<Activity> {
    const response = await apiClient.put(`/activities/${id}`, activityData);
    return response.data.data;
  }

  async deleteActivity(id: number): Promise<void> {
    if (!id || id === undefined || isNaN(Number(id)) || id <= 0) {
      throw new Error(`Invalid activity ID for deletion: ${id}`);
    }
    await apiClient.delete(`/activities/${id}`);
  }

  // ============================================================================
  // SUB-ACTIVITY OPERATIONS
  // ============================================================================

  async createSubActivity(parentId: number, activityData: Omit<CreateActivityDto, 'event_id' | 'parent_activity_id'>): Promise<Activity> {
    const response = await apiClient.post(`/activities/${parentId}/sub-activities`, activityData);
    return response.data.data;
  }

  async getSubActivities(parentId: number): Promise<Activity[]> {
    const response = await apiClient.get(`/activities/${parentId}/sub-activities`);
    return response.data.data || [];
  }

  // ============================================================================
  // LEAD MANAGEMENT
  // ============================================================================

  async assignLeads(activityId: number, guestIds: number[]): Promise<void> {
    const request: AssignLeadsRequest = { guest_ids: guestIds };
    await apiClient.post(`/activities/${activityId}/leads`, request);
  }

  async removeLead(activityId: number, guestId: number): Promise<void> {
    await apiClient.delete(`/activities/${activityId}/leads/${guestId}`);
  }

  // ============================================================================
  // KEY PARTICIPANT MANAGEMENT
  // ============================================================================

  async assignKeyParticipants(activityId: number, participants: KeyParticipantDto[]): Promise<void> {
    const request: AssignKeyParticipantsRequest = { participants };
    await apiClient.post(`/activities/${activityId}/key-participants`, request);
  }

  async removeKeyParticipant(activityId: number, guestId: number): Promise<void> {
    await apiClient.delete(`/activities/${activityId}/key-participants/${guestId}`);
  }

  // ============================================================================
  // VENDOR MANAGEMENT
  // ============================================================================

  async tagVendors(activityId: number, vendorIds: number[]): Promise<void> {
    const request: TagVendorsRequest = { vendor_ids: vendorIds };
    await apiClient.post(`/activities/${activityId}/vendors`, request);
  }

  async untagVendor(activityId: number, vendorId: number): Promise<void> {
    await apiClient.delete(`/activities/${activityId}/vendors/${vendorId}`);
  }

  // ============================================================================
  // STATUS MANAGEMENT
  // ============================================================================

  async updateStatus(activityId: number, status: string): Promise<Activity> {
    const request: UpdateStatusRequest = { status: status as any };
    const response = await apiClient.put(`/activities/${activityId}/status`, request);
    return response.data.data;
  }

  // ============================================================================
  // ITEM MANAGEMENT
  // ============================================================================

  async getItems(activityId: number): Promise<ActivityItem[]> {
    const response = await apiClient.get(`/activities/${activityId}/items`);
    return response.data.data || [];
  }

  async addItem(activityId: number, content: string): Promise<ActivityItem> {
    const request: AddItemRequest = { content };
    const response = await apiClient.post(`/activities/${activityId}/items`, request);
    return response.data.data;
  }

  async updateItem(itemId: number, content: string): Promise<ActivityItem> {
    const request: UpdateItemRequest = { content };
    const response = await apiClient.put(`/activities/items/${itemId}`, request);
    return response.data.data;
  }

  async deleteItem(itemId: number): Promise<void> {
    await apiClient.delete(`/activities/items/${itemId}`);
  }

  // ============================================================================
  // DUPLICATION
  // ============================================================================

  async duplicateActivity(activityId: number, newTimeline: TimelineDto): Promise<Activity> {
    const response = await apiClient.post(`/activities/${activityId}/duplicate`, newTimeline);
    return response.data.data;
  }

  // ============================================================================
  // REORDER OPERATIONS
  // ============================================================================

  async reorderActivities(eventId: number, reorderData: ReorderActivitiesRequest): Promise<void> {
    await apiClient.put(`/events/${eventId}/activities/reorder`, reorderData);
  }
}

export const activityService = new ActivityService();
